import { useCallback } from 'react';
import {
  useWithdrawSbp,
  useWithdrawSbpModals,
  type WithdrawSbpModalState,
} from '@/features/withdraw-sbp';
import { useTranslation } from 'react-i18next';
import type {
  TwoFAParams,
  WithdrawCommissionData,
  WithdrawSuccessResponse,
} from '@/shared/types';
import type { InfoDataGridData } from '@/shared/components';
import { WithdrawConfirmationModal } from '@/shared/components';
import { useWithdrawMFAFlow } from '@/shared/lib';
import type {
  WithdrawSbpBodyRequest,
  WithdrawSbpErrorResponse,
} from '../model';
import { useAfterOperationRefresh } from '@/entities/operation';

interface WithdrawConfirmationModalContainerProps {
  onBack: () => void;
  storeData: WithdrawSbpModalState;
  commissionData: WithdrawCommissionData;
  continueMFA?: boolean;
}

export const WithdrawConfirmationModalContainer = ({
  onBack,
  storeData,
  commissionData,
  continueMFA,
}: WithdrawConfirmationModalContainerProps) => {
  const { t } = useTranslation();
  const { openResultModal, openConfirmationModal } = useWithdrawSbpModals();

  const afterOperationRefresh = useAfterOperationRefresh();

  const currencyCode = storeData.selectedCurrency.code;
  const initialParams: WithdrawSbpBodyRequest = {
    amount: storeData.totalAmount.toString(),
    currencyCode,
    bankId: storeData.withdrawSbpFormData.bankId,
    paymentTypeCode: storeData.paymentTypeCode,
  };

  const injectMFA = useCallback(
    (
      params: WithdrawSbpBodyRequest,
      mfa: TwoFAParams
    ): WithdrawSbpBodyRequest => {
      return {
        ...params,
        twoFA: mfa,
      };
    },
    []
  );

  const { showMFA, startMFAVerification, renderMFAView } = useWithdrawMFAFlow<
    WithdrawSbpBodyRequest,
    WithdrawSuccessResponse,
    WithdrawSbpErrorResponse
  >({
    initialParams,
    injectMFA,
    useMutationHook: useWithdrawSbp,
    onSuccess: () => {
      openResultModal({ success: true });
      afterOperationRefresh();
    },
    onError: () => {
      openResultModal({ success: false });
    },
    onBack: openConfirmationModal,
    onClose: openConfirmationModal,
    onCancel: openConfirmationModal,
    onMFASetupComplete: () => {
      openConfirmationModal({ continueMFA: true });
    },
    modalTitle: 'withdrawMethods.crypto.confirmModal.title',
    continueMFA,
  });

  const onConfirmed = useCallback(() => {
    startMFAVerification();
  }, [startMFAVerification]);

  const addressInfo: InfoDataGridData = storeData
    ? [
        {
          key: t('withdrawMethods.sbp.phone'),
          value: storeData.withdrawSbpFormData.phoneNumber,
        },
        {
          key: t('withdrawMethods.amountInfo.totalWithdraw'),
          value: commissionData.totalWithdrawAmountStr,
        },
        {
          key: t('withdrawMethods.amountInfo.withdrawFee'),
          value: commissionData.feeStr,
        },
        {
          key: t('withdrawMethods.sbp.bank'),
          value: storeData.selectedBank.name,
        },
      ]
    : [];

  if (showMFA) {
    return renderMFAView();
  }

  return (
    <WithdrawConfirmationModal
      title={t('withdrawMethods.crypto.confirmModal.title')}
      onBack={onBack}
      infoData={[addressInfo]}
      onConfirmed={onConfirmed}
      isLoading={false}
      buttonText={t('withdrawMethods.withdrawButton')}
    />
  );
};
